import Application from '../Application';
import BaseView, { DomainModel3D } from './BaseView';
import CubbyView from './CubbyView';
import DividerView from './DividerView';
import DoorView from './DoorView';
import DrawerView from './DrawerView';
import GenericAssetView from './GenericAssetView';
import LightsView from './LightsView';
import ParametricPartView from './ParametricPartView';
import StackLayoutHelper, { DividerBox } from './StackLayoutHepler';
import logging from '../logging';
import { Mesh, MeshBasicMaterial, PlaneGeometry } from 'three';
import { Section } from '../domain';
import { deferred, zip } from '../utils';
import { gammaCorrect } from '../utils/gammaCorrect';

const log = logging('view');

const unitPlane = new PlaneGeometry(1, 1);
    // new BoxGeometry(1, 1, 1);
unitPlane.translate(0.5, 0.5, 0);

// Used on planes that provide hit detection for sections.
const hitMaterial = new MeshBasicMaterial({
    color: gammaCorrect(0x14a79d),
});

export default class SectionView extends BaseView {

    declare model: Section;

    sections: SectionView[];
    dividers: DividerView[];
    lights: LightsView;

    hit: Mesh;

    private helper: StackLayoutHelper;

    constructor(model: DomainModel3D, app: Application) {
        super(model, app);
        this.init();
    }

    make() {
        this.name = 'Section';

        this.sections = [];
        this.dividers = [];
        this.changeSize = this.changeSize.bind(this);

        this.helper = new StackLayoutHelper();
        this.helper.width = this.model.width;
        this.helper.height = this.model.height;
        this.helper.orientation = this.model.orientation == 'row' ? 'width' : 'height';
        this.helper.alignment = this.model.alignment;

        this.hit = new Mesh(unitPlane, hitMaterial);
        this.hit.visible = false;
        this.add(this.hit);

        this.hit.name = 'Section Hit';
        // Only "leaf" sections are directly selectable. Parent sections are
        // selected indirectly by clicking again on a leaf in order to select
        // the next parent
        this.hit.userData.isSelectable = (this.model.sections.length == 0);
        this.hit.userData.isSectionHit = true;

        this.highlights.add(this.hit);

        this.userData.isParametric = true;
        this.name = 'Section';

        for(let asset of this.model.assets) {
            let view = new GenericAssetView(asset, this.app);
            this.add(view);
        }
        for(let part of this.model.parametricParts) {
            let view = new ParametricPartView(part, this.app);
            this.add(view);
        }
        if(this.model.door) {
            this.add(new DoorView(this.model.door, this.app));
        }
        if(this.model.cubby) {
            this.add(new CubbyView(this.model.cubby, this.app));
        }
        if(this.model.lights) {
            this.lights = new LightsView(this.model.lights, this.app);
            this.add(this.lights);
        }
        for(let drawer of this.model.drawers) {
            this.add(new DrawerView(drawer, this.app));
        }

        if(this.model.sections.length == 0) {
            return; // nothing to do
        }
        if(this.model.sections.length == 1) {
            log.error("This Section model has a single child section and can't be"
                + " rendered", this.model);
        }
        else if(this.model.sections.length != 1 + this.model.dividers.length) {
            log.error("This Section model doesn't have n+1 sections for n dividers.", this.model);
        }
        else if(this.model.sections.length > 1) {

            for(const [subsection, divider] of
                zip(this.model.sections, this.model.dividers))
            {   
                const subsectionView = new SectionView(subsection, this.app);
                this.sections.push(subsectionView);
                this.add(subsectionView);
                this.helper.boxes.push(subsection);

                const dividerView = new DividerView(divider, this.app);
                this.dividers.push(dividerView);
                this.add(dividerView);
                this.helper.boxes.push(new DividerBox(divider));

                if(this.model.orientation == 'row') {
                    divider.height = this.model.height;
                    divider.orientation = "vertical";
                } else {
                    divider.width = this.model.width;
                    divider.orientation = "horizontal";
                }
            }

            // The last lonely section who has no companion divider
            const last = this.model.sections[this.model.sections.length - 1];
            const lastView = new SectionView(last, this.app);
            this.sections.push(lastView);
            this.add(lastView);
            this.helper.boxes.push(last);
        }
    }

    bind() {
        this.model.bind('width', this.changeSize);
        this.model.on('height', this.changeSize);
        this.model.on('depth', this.changeSize);
        this.model.on('thickness', () => {
            for(let subsection of this.model.sections) {
                subsection.trigger('thickness');
            }
            for(let divider of this.model.dividers) {
                divider.trigger('thickness');
            }
        });

        this.model.on('orientation', this.changeSize);
        this.model.on('alignment', this.changeSize);

        this.model.on('lights', () => {
            this.lights?.removeFromParent();
            if(this.model.lights) {
                this.lights = new LightsView(this.model.lights, this.app);
                this.add(this.lights);
                this.layoutLights();
            } else {
                this.lights = null;
                this.pipelineUpdate();
            }
        });

        // I'm not totally clear on why changeSize has to be deferred, but it
        // interferes with adding new shelves if it is not
        if(this.model.orientation == 'row') {
            for(let subsection of this.model.sections) {
                subsection.on('width', deferred(this.changeSize));
            }
        } else if(this.model.orientation == 'column') {
            for(let subsection of this.model.sections) {
                subsection.on('height', deferred(this.changeSize));
            }
        }
    }

    private changeSize() {
        this.layoutSelf();
        this.layoutDividers();
        this.layoutAssets();
        this.layoutParametricParts();
        if(this.model.cubby) this.layoutCubby();
        if(this.model.door) this.layoutDoor();
        if(this.model.lights) this.layoutLights();
        this.layoutDrawers();
    }

    private layoutSelf() {
        this.helper.width = this.model.width;
        this.helper.height = this.model.height;

        this.helper.orientation = this.model.orientation == 'row' ? 'width' : 'height';
        this.helper.alignment = this.model.alignment;

        if(this.model.sections.length > 1) {
            this.helper.boxes = [];
            for(const [subsection, divider] of
                zip(this.model.sections, this.model.dividers))
            {
                this.helper.boxes.push(subsection);
                this.helper.boxes.push(new DividerBox(divider));
            }
            const last = this.model.sections[this.model.sections.length - 1];
            this.helper.boxes.push(last);
        }

        for(let subsection of this.model.sections) {
            subsection.depth = this.model.depth;
        }

        this.helper.layout();
        this.hit.scale.set(this.model.width, this.model.height, 1);
    }

    private layoutAssets() {
        for(const asset of this.model.assets) {
            asset.position.x = this.model.width / 2;
            asset.width = this.model.width;
        }
    }

    private layoutDividers() {
        for(const divider of this.model.dividers) {
            divider.depth = this.model.depth;
            if(this.model.orientation == 'row') {
                divider.height = this.model.height;
                divider.orientation = "vertical";
            } else {
                divider.width = this.model.width;
                divider.orientation = "horizontal";
            }
        }
    }

    private layoutParametricParts() {
        for(let part of this.model.parametricParts) {
            part.width = this.model.width;
            part.depth = this.model.depth;
        }
    }

    private layoutCubby() {
        this.model.cubby.width = this.model.width;
        this.model.cubby.height = this.model.height;
        this.model.cubby.depth = this.model.depth;
    }

    private layoutDoor() {
        this.model.door.width = this.model.width + 0.5;
        this.model.door.height = this.model.height + 6/8;
        this.model.door.position.x = -0.25;
        this.model.door.position.y = -3/8;
        this.model.door.position.z = this.model.depth;
    }

    private layoutLights() {
        this.model.lights.width = this.model.width;
        this.model.lights.height = this.model.height;
        this.model.lights.depth = this.model.depth - 3;
    }

    private layoutDrawers() {
        for(let drawer of this.model.drawers) {
            drawer.width = this.model.width + 0.5;
            drawer.depth = this.model.depth;
            drawer.position.x = -0.25;
            drawer.position.z = this.model.depth;
        }
    }
}
